/**
 * Step 7 · Team & Reporting
 * CP/QP identity, reporting standard, report type, effective/data-cutoff dates.
 */
import { useQuery } from "@tanstack/react-query";
import { User, Certificate, Calendar, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SearchableSelect from "../parts/SearchableSelect";
import { FieldRow, StepHeader } from "./Step1Identity";

const REPORT_TYPES = [
  { id: "CPR",           label: "Competent Person's Report" },
  { id: "MRE",           label: "Mineral Resource Estimate" },
  { id: "MRE_MRR",       label: "MRE + Mineral Reserve" },
  { id: "EXPLORATION",   label: "Exploration Results" },
  { id: "PEA",           label: "Preliminary Economic Assessment" },
  { id: "PFS",           label: "Pre-Feasibility Study" },
  { id: "DFS",           label: "Definitive Feasibility Study" },
  { id: "BFS",           label: "Bankable Feasibility Study" },
  { id: "DUE_DILIGENCE", label: "Due Diligence" },
  { id: "INTERNAL",      label: "Internal — not for public reporting" },
];

export default function Step7Team({ form, set }) {
  const standardsQ = useQuery({
    queryKey: ["master-reporting-standards"],
    queryFn:  () => api.get("/master/reporting-standards").then((r) => r.data.results),
    staleTime: 5 * 60_000,
  });
  return (
    <section className="space-y-4" data-testid="step-7-team">
      <StepHeader step="Step 7" title="Team & Reporting"
        subtitle="Competent Person, reporting standard and effective date. These drive mandatory report sections and approval workflows." />

      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        <FieldRow icon={User} label="Competent / Qualified Person Name" required
                     hint="Signs off the resource statement.">
          <input type="text" value={form.cp_name || ""}
                   onChange={(e) => set({ cp_name: e.target.value })}
                   placeholder="e.g. Dr. J. Smith"
                   data-testid="input-cp-name"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>
        <FieldRow icon={Certificate} label="Registration / Membership Number"
                     hint="e.g. MAusIMM CP 123456, PGeo #ABC-7890.">
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={form.cp_registration || ""}
                     onChange={(e) => set({ cp_registration: e.target.value })}
                     placeholder="Registration"
                     data-testid="input-cp-registration"
                     className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
            <input type="text" value={form.cp_membership || ""}
                     onChange={(e) => set({ cp_membership: e.target.value })}
                     placeholder="Institute / body"
                     data-testid="input-cp-membership"
                     className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
          </div>
        </FieldRow>
        <FieldRow icon={User} label="Independence Declaration">
          <select value={form.cp_independence || ""}
                    onChange={(e) => set({ cp_independence: e.target.value })}
                    data-testid="select-cp-independence"
                    className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]">
            <option value="">— Not stated —</option>
            <option value="independent">Independent</option>
            <option value="non_independent">Non-independent (in-house)</option>
          </select>
        </FieldRow>

        <FieldRow icon={Sparkle} label="Primary Reporting Standard" required>
          <SearchableSelect
            testId="select-reporting-standard"
            value={form.reporting_standard}
            options={(standardsQ.data || []).map((s) => ({
              value: s.id, label: s.title, hint: s.code, meta: s.jurisdiction,
            }))}
            onChange={(v) => set({ reporting_standard: v })}
            placeholder="JORC · NI 43-101 · S-K 1300 · SAMREC · PERC …"
            groupBy="meta"
          />
        </FieldRow>
        <FieldRow icon={Sparkle} label="Secondary Reporting Standard" hint="Optional — for dual-compliance reports.">
          <SearchableSelect
            testId="select-secondary-standard"
            value={form.secondary_standard || ""}
            options={[{ value: "", label: "— None —" }].concat(
              (standardsQ.data || []).map((s) => ({ value: s.id, label: s.title, hint: s.code, meta: s.jurisdiction })))}
            onChange={(v) => set({ secondary_standard: v || undefined })}
            placeholder="— none —"
            groupBy="meta"
          />
        </FieldRow>

        <FieldRow icon={Sparkle} label="Report Type" hint="Report type controls chapter set + appendices.">
          <select value={form.report_type || "CPR"}
                    onChange={(e) => set({ report_type: e.target.value })}
                    data-testid="select-report-type"
                    className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]">
            {REPORT_TYPES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </FieldRow>

        <FieldRow icon={Calendar} label="Effective Date"
                     hint="The date the resource statement is valid as-of.">
          <input type="date" value={form.effective_date || ""}
                   onChange={(e) => set({ effective_date: e.target.value })}
                   data-testid="input-effective-date"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>
        <FieldRow icon={Calendar} label="Data Cut-off Date"
                     hint="The date past which no new data is included.">
          <input type="date" value={form.data_cutoff_date || ""}
                   onChange={(e) => set({ data_cutoff_date: e.target.value })}
                   data-testid="input-data-cutoff-date"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>
      </div>
    </section>
  );
}
